/* eslint-disable max-lines */
/* eslint-disable max-len */

import { ArrowPathIcon, CheckIcon, PencilIcon, StarIcon, TrashIcon } from "@heroicons/react/24/outline";
import { IEsimPackage } from "../../interfaces/esim/EsimPackages.types";
import DeleteModal from "../ui/DeleteModal";
import IconBtn from "../ui/IconBtn";

interface PackageCardProps {
  pkg: IEsimPackage;
  editingPackage: IEsimPackage | null;
  tempChanges: Partial<IEsimPackage>;
  onStartEditing: (pkg: IEsimPackage) => void;
  onFinishEditing: () => void;
  onDelete: (id: number) => void;
  onPackageUpdate: (packageId: number, field: string, value: string) => void;
}

const PackageDeleteModal: React.FC<{ pkg: IEsimPackage; onDelete: (id: number) => void }> = ({
  pkg,
  onDelete
}) => (
  <DeleteModal
    params={pkg.id}
    title="Delete Package"
    paragraph="Are you sure you want to delete this package? This action cannot be undone."
    handleDelete={() => {
      onDelete(pkg.id);
      return Promise.resolve({ data: null, status: 200, statusText: "OK" });
    }}
    successMessage="Package deleted successfully"
    errorMessage="Error deleting package"
    loadingMessage="Deleting package..."
    indicator={
      <IconBtn className="w-8 h-8 text-red-500 hover:text-red-600">
        <TrashIcon className="w-5 h-5" />
      </IconBtn>
    }
  />
);

export const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  editingPackage,
  tempChanges,
  onStartEditing,
  onFinishEditing,
  onDelete,
  onPackageUpdate
}) => (
  <div
    key={pkg.id}
    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg
      transition-all duration-300 transform hover:-translate-y-1"
    style={{
      borderTop: `4px solid ${pkg.gradientColorHex}`
    }}
  >
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center">
            <img
              src={pkg.imageUrl}
              alt={pkg.name}
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{pkg.name}</h3>
            <div className="flex flex-col gap-1 mt-1">
              {pkg.isRecommended && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs
                font-medium bg-green-100 text-green-800">
                  Recommended
                </span>
              )}
              {pkg.isUnlimited && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs
                font-medium bg-blue-200 text-green-800">
                  Unlimited
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editingPackage?.id === pkg.id ? (
            <>
              <IconBtn
                onClick={() => onPackageUpdate(pkg.id, "isRecommended",
                  (!tempChanges.isRecommended).toString())}
                className={`w-10 h-10 rounded-lg transition-colors duration-200 ${
                  tempChanges.isRecommended
                    ? "text-orange-300 hover:text-orange-500 hover:bg-yellow-50"
                    : "text-gray-400 hover:text-gray-500 hover:bg-gray-50"
                }`}
              >
                <StarIcon className="w-5 h-5" />
              </IconBtn>
              <IconBtn
                onClick={() => onPackageUpdate(pkg.id, "isUnlimited",
                  (!tempChanges.isUnlimited).toString())}
                className={`w-10 h-10 rounded-lg transition-colors duration-200 ${
                  tempChanges.isUnlimited
                    ? "text-blue-200 hover:text-blue-300 hover:bg-blue-100"
                    : "text-gray-400 hover:text-gray-500 hover:bg-gray-50"
                }`}
              >
                <ArrowPathIcon className="w-5 h-5" />
              </IconBtn>
              <IconBtn
                onClick={onFinishEditing}
                className="w-10 h-10 text-green-500 hover:text-green-600 hover:bg-green-50
                rounded-lg transition-colors duration-200"
              >
                <CheckIcon className="w-5 h-5" />
              </IconBtn>
            </>
          ) : (
            <IconBtn
              onClick={() => onStartEditing(pkg)}
              className="w-10 h-10 text-blue-500 hover:text-blue-600 hover:bg-blue-50
              rounded-lg transition-colors duration-200"
            >
              <PencilIcon className="w-5 h-5" />
            </IconBtn>
          )}
          {editingPackage?.id !== pkg.id && (
            <PackageDeleteModal pkg={pkg} onDelete={onDelete} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <span className="text-sm text-gray-600 block mb-1">Data</span>
          <div className="flex items-center gap-2">
            {editingPackage?.id === pkg.id ? (
              <input
                type="text"
                value={tempChanges.totalGB ?? ""}
                onChange={(e) => onPackageUpdate(pkg.id, "totalGB", e.target.value)}
                className="w-full p-2 border rounded-lg text-right bg-white
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <span className="text-xl font-semibold text-gray-900">{pkg.totalGB} GB</span>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <span className="text-sm text-gray-600 block mb-1">Validity</span>
          <div className="flex items-center gap-2">
            {editingPackage?.id === pkg.id ? (
              <input
                type="text"
                value={tempChanges.validityDays ?? ""}
                onChange={(e) => onPackageUpdate(pkg.id, "validityDays", e.target.value)}
                className="w-full p-2 border rounded-lg text-right bg-white
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <span className="text-xl font-semibold text-gray-900">{pkg.validityDays} days</span>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <span className="text-sm text-gray-600 block mb-1">Price</span>
          <div className="flex items-center gap-2">
            {editingPackage?.id === pkg.id ? (
              <input
                type="text"
                inputMode="decimal"
                value={tempChanges.cost ?? ""}
                onChange={(e) => onPackageUpdate(pkg.id, "cost", e.target.value)}
                placeholder="0.00"
                className="w-full p-2 border rounded-lg text-right bg-white
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <span className="text-xl font-semibold text-gray-900">€{pkg.cost.toFixed(2)}</span>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <span className="text-sm text-gray-600 block mb-1">Old Price</span>
          <div className="flex items-center gap-2">
            {editingPackage?.id === pkg.id ? (
              <input
                type="text"
                inputMode="decimal"
                value={tempChanges.oldCost ?? ""}
                onChange={(e) => onPackageUpdate(pkg.id, "oldCost", e.target.value)}
                placeholder="0.00"
                className="w-full p-2 border rounded-lg text-right bg-white
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <span className="text-xl font-semibold text-gray-900">€{pkg.oldCost.toFixed(2)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);
